import { AddToListButton } from '@app/components/AddToListButton';
import { ProductCard } from '@app/components/ProductCard';
import { SimilarDesignItem } from '@app/data/clients/rbServerSideClient/queries/GetSimilarDesigns/types';
import { useListsInclusions } from '@app/hooks/useListsInclusions';
import {
  parseSimilarDesignItemToAnalyticsEcommerceItem,
  trackGA4ViewItemListEvent,
} from '@app/pages/i/analytics';
import { getPreviewUrl } from '@app/utils/common';
import { getPriceComponent } from '@app/utils/getPriceComponent';
import { SearchUUIDStorageKey } from '@app/utils/searchUUIDStorage';
import { Box, Grid } from '@redbubble/design-system';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import {
  type Item as AnalyticsEcommerceItem,
  AnalyticsItemsList,
} from '@redbubble/next-analytics/gtm';
import {
  RSProductClickedLocation,
  RSProductClickedPageTypeKey,
} from '@redbubble/next-provider/rudderstack';
import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { type ListsButtonInventoryItem } from 'types/shop-bff/types';
import styles from './SimilarDesigns.module.scss';

const PREVIEW_TYPE_ID = 'product_close';

export const messages = defineMessages({
  byArtist: {
    defaultMessage: 'By {artistName}',
  },
});

export interface SimilarDesignsProps {
  similarDesigns: SimilarDesignItem[];
  rsPageType?: RSProductClickedPageTypeKey;
}

export const SimilarDesigns: React.FC<SimilarDesignsProps> = ({
  similarDesigns,
  rsPageType,
}) => {
  const intl = useIntl();
  const { ref, inView } = useInView();
  const hasFired = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
  useEffect(() => {
    // reset hasfired if similar designs have changed
    hasFired.current = false;
  }, [similarDesigns]);

  useEffect(() => {
    if (!hasFired.current && inView && similarDesigns) {
      const analyticsItems = similarDesigns
        .map((similarDesignItem, index) => {
          return parseSimilarDesignItemToAnalyticsEcommerceItem({
            similarDesignItem,
            index,
          });
        })
        .filter(
          (
            item: AnalyticsEcommerceItem | null
          ): item is NonNullable<AnalyticsEcommerceItem> => !!item
        );

      trackGA4ViewItemListEvent({
        items: analyticsItems,
        listID: AnalyticsItemsList.SimilarDesignsATCDrawer,
        topic: '',
        searchUUIDStorageKey: SearchUUIDStorageKey.LocationPathname,
      });
      hasFired.current = true;
    }
  }, [similarDesigns, inView]);

  const { listsInclusions, refresh: refreshListInclusions } =
    useListsInclusions();

  useEffect(() => {
    refreshListInclusions(
      similarDesigns.map(({ inventoryItem }) => inventoryItem)
    );
  }, [refreshListInclusions, similarDesigns]);

  return (
    <Box className={styles.grid} data-testid="similar-designs" ref={ref}>
      <Grid columns={2}>
        {similarDesigns.map((similarDesign, index) => {
          const item = similarDesign.inventoryItem;
          if (!item) return null;

          const workTitle = item.work.title;
          const workId = item.work.id;
          const artistName = item.work.artistShopDisplayName;
          const volumeDiscountThresholds =
            item.volumeDiscount?.thresholds || [];
          const { id, price, description } = item;

          const link = item.productPageUrl
            ? new URL(item.productPageUrl).pathname
            : undefined;

          const imageSrc = getPreviewUrl(item, PREVIEW_TYPE_ID);
          if (!workTitle || !workId || !imageSrc) return null;

          return (
            <ProductCard
              key={id}
              title={`${workTitle} ${description}`}
              workTitle={workTitle}
              link={link}
              // TODO: When IHS schema updates, remove nullish option
              artistName={artistName ?? undefined}
              caption={intl.formatMessage(messages.byArtist, {
                artistName,
              })}
              imageSrc={imageSrc}
              matureContent={item.work.matureContent}
              analyticsIndex={index}
              analyticsComponentID={AnalyticsItemsList.SimilarDesignsATCDrawer}
              primaryAction={
                <AddToListButton
                  inventoryItem={item as ListsButtonInventoryItem}
                  workId={workId}
                  listsInclusions={listsInclusions}
                  analyticsComponentID={
                    AnalyticsItemsList.SimilarDesignsATCDrawer
                  }
                  analyticsIndex={index}
                  searchUUIDStorageKey={SearchUUIDStorageKey.LocationPathname}
                />
              }
              stockStatus={item.stockStatus}
              rsLocation={RSProductClickedLocation.SimilarDesignsATCDrawer}
              rsPageType={rsPageType}
            >
              {getPriceComponent(volumeDiscountThresholds, price)}
            </ProductCard>
          );
        })}
      </Grid>
    </Box>
  );
};
